import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import {
  useSpring, animated, easings,
} from 'react-spring';
import { useDropzone } from 'react-dropzone';
import ResizeObserver from 'react-resize-observer';
import { Formik } from 'formik';

import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import { saveWorkoutTemplate } from '../ducks/calendarActions';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import { showErrorToast } from '../../../shared/utils/toastManager';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};
`;

const ModalTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const HelperText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  line-height: 1.4;
  margin-bottom: 24px;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
`;

const PrimaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: #444444;
  color: white;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
  margin-bottom: 8px;
`;

const SecondaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: white;
  color: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

// First Slide Components

const DateLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 8px;
  display: block;
`;

// Second Slide Components
const FormGroup = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const Label = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  display: block;
  margin-top: 10px;
  margin-bottom: 0px;
`;

const Input = styled('input')`
  width: 100%;
  height: 40px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: #444444;
  font-family: 'Nunito Sans';

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const TextArea = styled('textarea')`
  width: 100%;
  height: 100px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  color: #444444;
  font-family: 'Nunito Sans';
  resize: none;

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const CheckboxContainer = styled('div')`
  display: flex;
  gap: 24px;
  margin-top: 16px;
`;

const CheckboxGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled('input')`
  cursor: pointer;
`;

const CheckboxLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
  cursor: pointer;
`;

const ImageUploadArea = styled('div')`
  width: 100%;
  height: 120px;
  border: 1px dashed ${(props) => (props.isDragActive ? '#444444' : '#EEEEEE')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.isDragActive ? 'rgba(68, 68, 68, 0.05)' : 'white')};
  position: relative;
  overflow: hidden;
  margin-top: 8px;

  &:hover {
    border-color: #444444;
    background: rgba(68, 68, 68, 0.05);
  }
`;

const ImagePreviewContainer = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F5F5F5;
`;

const ImagePreview = styled('img')`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const RemoveButton = styled('div')`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ImageUploadText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  margin-top: 8px;
`;

const DatePickerRow = styled('div')`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const FileTypeText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9E9E9E;
`;

const TextAndPicker = styled('div')`
  display: flex;
  flex-direction: column;
  width: 48%;
`;

const formatDateString = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');

const SaveWorkoutModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);

  // Use modalData.date if provided (from week view), otherwise use selectedDate from Redux.
  const initialDate = modalData?.date ? new Date(modalData.date) : new Date(selectedDate);

  const initialValues = {
    startDate: initialDate,
    endDate: initialDate,
    title: '',
    description: '',
    saveWithTitles: true,
    saveWithPrescribedLoads: false,
  };

  // Async upload state lives outside Formik — the file upload is imperative
  // and the dropzone hook is set up at the top of the component.
  const [headerImage, setHeaderImage] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Wizard navigation and animation state — UI, not submitted data.
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hideOverflow, setHideOverflow] = useState(true);

  const showSlide1 = currentSlide === 0;
  const showSlide2 = currentSlide === 1;

  // Animations live at the top of the component — hooks can't run inside the
  // Formik render-prop because Formik 1.5 is a class component.
  const slide1Animation = useSpring({
    opacity: showSlide1 ? 1 : 0,
    transform: showSlide1 ? 'translateX(0%)' : 'translateX(-100%)',
    config: { duration: 300, easing: easings?.easeInOutQuad },
    immediate: isResizing,
  });

  const slide2Animation = useSpring({
    opacity: showSlide2 ? 1 : 0,
    transform: showSlide2 ? 'translateX(0%)' : 'translateX(100%)',
    config: { duration: 300, easing: easings?.easeInOutQuad },
    immediate: isResizing,
    onRest: () => {
      // Allow overflow for date-picker popovers once animation settles.
      setHideOverflow(false);
    },
  });

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setUploadError('Please upload a valid image file');
      return;
    }

    const file = acceptedFiles[0];
    setUploadError(null);
    setHeaderImage({
      file,
      preview: URL.createObjectURL(file),
      mediaUrl: null,
    });

    const formData = new FormData();
    formData.append('image', file);
    setIsUploading(true);
    axiosAuthed.post('/media/upload/image', formData)
      .then((response) => {
        setIsUploading(false);
        setHeaderImage((prev) => {
          if (!prev) return null;
          return { ...prev, mediaUrl: response.data.mediaUrl, mediaId: response.data.id };
        });
      })
      .catch(() => {
        setIsUploading(false);
        setUploadError('Failed to upload image');
        setHeaderImage(null);
      });
  }, [currentUser]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    multiple: false,
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setHeaderImage(null);
    setUploadError(null);
  };

  // Final save — Formik's onSubmit. Reads headerImage via closure since the
  // upload lifecycle is imperative state, not form data.
  const handleSave = (values, { setSubmitting }) => {
    if (!values.title.trim()) {
      showErrorToast('Title is required');
      setSubmitting(false);
      return;
    }
    if (!values.startDate) {
      showErrorToast('Start date is required');
      setSubmitting(false);
      return;
    }
    if (!values.endDate) {
      showErrorToast('End date is required');
      setSubmitting(false);
      return;
    }
    if (headerImage && !headerImage.mediaUrl) {
      showErrorToast('Image is still uploading. Please wait.');
      setSubmitting(false);
      return;
    }

    const template = {
      startDate: values.startDate,
      endDate: values.endDate,
      title: values.title,
      description: values.description || 'description',
      saveWithTitles: values.saveWithTitles,
      saveWithPrescribedLoads: values.saveWithPrescribedLoads,
      ...(headerImage?.mediaId && { mediaId: headerImage.mediaId }),
    };

    dispatch(saveWorkoutTemplate(currentUser.accountCode, template))
      .then(() => {
        closeModal();
      })
      .catch(() => {
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSave}
      render={({
        values, setFieldValue, handleSubmit, isSubmitting,
      }) => {
        const handleStartDateChange = (date) => {
          setFieldValue('startDate', date ? moment(date).toDate() : null);
        };
        const handleEndDateChange = (date) => {
          setFieldValue('endDate', date ? moment(date).toDate() : null);
        };

        const totalDaysCount = values.startDate && values.endDate
          ? moment(values.endDate).startOf('day').diff(moment(values.startDate).startOf('day'), 'days') + 1
          : 0;
        const dateRangeError = totalDaysCount > 365
          ? 'You cannot save a program longer than 365 days'
          : null;

        const handleNext = () => {
          if (!values.startDate || !values.endDate) {
            showErrorToast('Please select both start and end dates');
            return;
          }
          if (totalDaysCount > 365) {
            return;
          }
          setHideOverflow(true);
          setCurrentSlide(1);
        };

        const handleBack = () => {
          setHideOverflow(true);
          setCurrentSlide(0);
        };

        return (
          <ModalContainer hideOverflow={hideOverflow}>

            <ResizeObserver
              onResize={() => {
                setIsResizing(true);
                setTimeout(() => {
                  setIsResizing(false);
                }, 300);
              }}
            />

            <ModalTitle>Save to Program Library</ModalTitle>
            <HelperText>
              Saving to the Program Library allows you to re-use a set of built out workouts that can be
              loaded onto another calendar or assigned to your athletes outside of the calendar.
            </HelperText>

            {showSlide1 && (
              <animated.div style={slide1Animation}>
                <div style={{ width: '100%' }}>
                  <DatePickerRow>
                    <TextAndPicker>
                      <DateLabel>Start Date</DateLabel>
                      <TBDatePicker
                        activeDate={formatDateString(values.startDate)}
                        setActiveDate={handleStartDateChange}
                        additionalStyles={{
                          margin: '8px 0 0 0',
                          maxWidth: '100%',
                        }}
                        showDropShadow={false}
                        showIcon={false}
                        maxDate={null}
                      />
                    </TextAndPicker>

                    <TextAndPicker>
                      <DateLabel>End Date</DateLabel>
                      <TBDatePicker
                        activeDate={formatDateString(values.endDate)}
                        setActiveDate={handleEndDateChange}
                        additionalStyles={{
                          margin: '8px 0 0 0',
                          maxWidth: '100%',
                        }}
                        showDropShadow={false}
                        showIcon={false}
                        maxDate={null}
                      />
                    </TextAndPicker>
                  </DatePickerRow>

                  {dateRangeError && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%',
                    }}
                    >
                      <Text color='#E53935' fontSize='12px' style={{ marginTop: '8px' }}>{dateRangeError}</Text>
                    </div>
                  )}
                  <ButtonsContainer>
                    <PrimaryButton type='button' onClick={handleNext}>NEXT</PrimaryButton>
                    <SecondaryButton type='button' onClick={closeModal}>CANCEL</SecondaryButton>
                  </ButtonsContainer>
                </div>
              </animated.div>
            )}

            {showSlide2 && (
              <animated.div style={slide2Animation}>
                <div style={{ width: '100%' }}>
                  <FormGroup>
                    <Label>Title</Label>
                    <Input
                      type='text'
                      value={values.title}
                      onChange={(e) => setFieldValue('title', e.target.value)}
                      placeholder='ex. Hypertrophy - Lower Body'
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Description</Label>
                    <TextArea
                      value={values.description}
                      onChange={(e) => setFieldValue('description', e.target.value)}
                      placeholder='Enter a description of this program'
                    />
                  </FormGroup>

                  <CheckboxContainer>
                    <CheckboxGroup>
                      <Checkbox
                        type='checkbox'
                        id='save-titles'
                        checked={values.saveWithTitles}
                        onChange={() => setFieldValue('saveWithTitles', !values.saveWithTitles)}
                      />
                      <CheckboxLabel htmlFor='save-titles'>Save workout titles and colors</CheckboxLabel>
                    </CheckboxGroup>

                    <CheckboxGroup>
                      <Checkbox
                        type='checkbox'
                        id='save-loads'
                        checked={values.saveWithPrescribedLoads}
                        onChange={() => setFieldValue('saveWithPrescribedLoads', !values.saveWithPrescribedLoads)}
                      />
                      <CheckboxLabel htmlFor='save-loads'>Save prescribed loads</CheckboxLabel>
                    </CheckboxGroup>
                  </CheckboxContainer>

                  <FormGroup>
                    <Label>Header Image</Label>
                    <Text style={{ fontSize: '12px', color: '#9E9E9E', marginBottom: '8px' }}>
                      Recommended size: 1060 x 420
                    </Text>
                    <ImageUploadArea {...getRootProps()} isDragActive={isDragActive}>
                      <input {...getInputProps()} />
                      {headerImage ? (
                        <ImagePreviewContainer>
                          <ImagePreview src={headerImage.preview} alt='Header preview' />
                          {isUploading ? (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(255,255,255,0.7)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            >
                              <Text fontSize='14px' color='#444444'>Uploading...</Text>
                            </div>
                          ) : (
                            <RemoveButton onClick={handleRemoveImage}>
                              <IcomoonReact iconSet={iconSet} icon='remove' size={12} color='#FFFFFF' />
                            </RemoveButton>
                          )}
                        </ImagePreviewContainer>
                      ) : (
                        <>
                          <ImageUploadText>Click or drag image here.</ImageUploadText>
                          <FileTypeText>Accepted file types: jpg, jpeg, png</FileTypeText>
                        </>
                      )}
                    </ImageUploadArea>
                    {uploadError && <Text color='#E53935' fontSize='12px' style={{ marginTop: '8px' }}>{uploadError}</Text>}
                    <Text color='#9E9E9E' fontSize='12px' style={{ marginTop: '8px' }}>
                      Note: Recurring workout items cannot currently be saved.
                      {' '}
                      <a href='https://support.teambuildr.com/article/t5talti5mh-recurring-workout-items' target='_blank' rel='noopener noreferrer' style={{ color: '#0056b3', fontSize: '12px' }}>Click here</a>
                      {' '}
                      to learn more about recurring items.
                    </Text>
                  </FormGroup>

                  <ButtonsContainer>
                    <PrimaryButton
                      type='button'
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting || isUploading}
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isSubmitting ? 'SAVING...' : isUploading ? 'UPLOADING...' : 'SAVE'}
                    </PrimaryButton>
                    <SecondaryButton type='button' onClick={handleBack}>BACK</SecondaryButton>
                  </ButtonsContainer>
                </div>
              </animated.div>
            )}
          </ModalContainer>
        );
      }}
    />
  );
};

SaveWorkoutModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isFromWeekView: PropTypes.bool,
  }),
};

SaveWorkoutModal.defaultProps = {
  modalData: null,
};

export default SaveWorkoutModal;
